import { useMutation, useQuery } from '@tanstack/react-query';
import { useSetState } from 'ahooks';
import { Button, Checkbox, Col, Drawer, Flex, Form, Input, Row, Select } from 'antd';
import { get, isNull, omitBy } from 'lodash';
import { forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * APIs
 */
import { roles } from '@/apis/role';
import { cUser, uUser } from '@/apis/user';

/**
 * Components
 */
import ComponentUpload from '@/components/upload';

/**
 * Types
 */
import type { UserWithRoleUsersDto } from '@/dtos/user.dto';
import type { DrawerProps, FormProps } from 'antd';

export type Action =
    | {
          update: (data: UserWithRoleUsersDto) => void;
      }
    | undefined;

type Props = {
    onFinish?: (data: UserWithRoleUsersDto) => void;
};

type State = {
    open: boolean;
    type: 'create' | 'update';
};

const ComponentCreateUpdate = forwardRef<Action, Props>((props, ref) => {
    /**
     * Hooks
     */
    const [form] = Form.useForm();

    const { t } = useTranslation();

    /**
     * States
     */
    const [state, setState] = useSetState<State>({
        open: false,
        type: 'create',
    });

    /**
     * Requests
     */
    const { isPending: pendingCUser, mutate: mutateCUser } = useMutation({
        mutationKey: ['cUser'],
        mutationFn: cUser,
        onSuccess: ({ data: { data } }) => {
            // 关闭抽屉
            setState({
                open: false,
            });

            // 执行回调
            props?.onFinish?.(data);
        },
    });

    const { isPending: pendingUUser, mutate: mutateUUser } = useMutation({
        mutationKey: ['uUser'],
        mutationFn: uUser,
        onSuccess({ data: { data } }) {
            // 关闭抽屉
            setState({
                open: false,
            });

            // 执行回调
            props?.onFinish?.(data);
        },
    });

    const { data: dataRoles, isLoading: loadingRoles } = useQuery({
        queryKey: ['roles'],
        retry: false,
        queryFn: () => roles({ size: 9999 }),
    });

    /**
     * ChildrenProps
     */
    const drawerProps: DrawerProps = {
        closeIcon: false,
        extra: (
            <Flex gap={8}>
                <Button
                    onClick={() => {
                        setState({
                            open: false,
                        });
                    }}
                >
                    {t('cancel')}
                </Button>

                <Button loading={pendingCUser || pendingUUser} type="primary" onClick={form.submit}>
                    {t('save')}
                </Button>
            </Flex>
        ),
        maskClosable: false,
        open: state.open,
        size: 'large',
        title: state.type === 'create' ? t('create') : t('edit'),
        afterOpenChange: (open) => {
            if (open) return;

            form.resetFields();
        },
        onClose: () => {
            setState({
                open: false,
            });
        },
    };

    const formProps: FormProps = {
        autoComplete: 'off',
        form,
        initialValues: {
            gender: 'secret',
        },
        layout: 'vertical',
        onFinish: (values) => {
            values.avatar = get(values, 'avatar[0].data.path');

            if (state.type === 'create') {
                return mutateCUser(values);
            }

            if (state.type === 'update') {
                return mutateUUser(values);
            }
        },
    };

    /**
     * Handles
     */
    useImperativeHandle(ref, () => {
        return {
            update: (data) => {
                form.setFieldsValue({
                    ...omitBy(data, isNull),
                    avatar: data.avatar
                        ? [
                              {
                                  url: `${import.meta.env.VITE_CDN}/${data.avatar}`,
                              },
                          ]
                        : undefined,
                    email: undefined,
                    mobile: undefined,
                    password: undefined,
                    role_ids: data.roleUsers?.map((item) => item.role_id) || [],
                });

                setState({
                    open: true,
                    type: 'update',
                });
            },
        };
    });

    return (
        <>
            <Button
                type="primary"
                onClick={() => {
                    setState({
                        open: true,
                        type: 'create',
                    });
                }}
            >
                {t('create')}
            </Button>

            <Drawer {...drawerProps}>
                <Form {...formProps}>
                    <Form.Item hidden name="id">
                        <Input />
                    </Form.Item>

                    <Form.Item hidden name="version">
                        <Input />
                    </Form.Item>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item label={t('nickname')} name="nickname" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item label={t('gender')} name="gender">
                                <Select
                                    options={[
                                        {
                                            label: t('gender_secret'),
                                            value: 'secret',
                                        },
                                        {
                                            label: t('gender_male'),
                                            value: 'male',
                                        },
                                        {
                                            label: t('gender_female'),
                                            value: 'female',
                                        },
                                    ]}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item
                                label={t('avatar')}
                                name="avatar"
                                valuePropName="fileList"
                                getValueFromEvent={(e) => e.fileList}
                            >
                                <ComponentUpload app="dashboard" module="avatar" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={12}>
                            <Form.Item label={t('username')} name="username" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label={t('email')}
                                name="email"
                                rules={[{ type: 'email' }]}
                                tooltip={t('form_tooltip')}
                            >
                                <Input />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item label={t('password')} name="password" tooltip={t('form_tooltip')}>
                                <Input.Password autoComplete="new-password" />
                            </Form.Item>

                            <Form.Item label={t('mobile')} name="mobile" tooltip={t('form_tooltip')}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label={t('bio')} name="bio">
                        <Input.TextArea autoSize={{ maxRows: 4, minRows: 4 }} />
                    </Form.Item>

                    <Form.Item label={t('status')}>
                        <Row gutter={[8, 8]}>
                            <Col span={12}>
                                <Form.Item name="is_disabled" noStyle={true} valuePropName="checked">
                                    <Checkbox>{t('status_disabled')}</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form.Item>

                    <Form.Item label={t('roles')} name="role_ids">
                        <Select
                            loading={loadingRoles}
                            mode="multiple"
                            options={get(dataRoles, 'data.data', []).map((role) => ({
                                label: role.name,
                                value: role.id,
                            }))}
                        />
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
});

export default ComponentCreateUpdate;
